import React from 'react';
import { Typography, COLORS, Tooltip, IconButton, TableRow, TableCell } from 'pvs-design-system';
import { Box, Stack } from '@mui/material';
import Folder from '@mui/icons-material/Folder';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import type { Category } from 'types';
import { LIBRARY_ROW_SX } from 'constants/styles';

interface FolderTableRowProps {
  folder: Category;
  onOpen: (folder: Category, e?: React.MouseEvent) => void;
  onEdit: (folder: Category) => void;
  onDelete: (folder: Category) => void;
  /** Show the edit action (already gated on manage mode + permissions by the caller). */
  canEdit: boolean;
  /** Show the delete action (already gated on manage mode + permissions by the caller). */
  canDelete: boolean;
}

/** A folder row in the list/table view of the browse and home pages. */
const FolderTableRow: React.FC<FolderTableRowProps> = ({
  folder,
  onOpen,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
}) => {
  const displayName = folder.name;

  return (
  <TableRow
    tabIndex={0}
    role="button"
    onClick={(e: React.MouseEvent) => onOpen(folder, e)}
    onKeyDown={(e: React.KeyboardEvent) => {
      // Only act when the row itself is focused, so Enter/Space on a nested
      // Edit/Delete button doesn't bubble up and also open the folder.
      if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onOpen(folder);
      }
    }}
    sx={LIBRARY_ROW_SX}
  >
    <TableCell align="center">
      <Box
        title="Folder"
        sx={{ width: 32, height: 32, borderRadius: 8, bgcolor: COLORS.blue[100], color: COLORS.blue[700], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mx: 'auto' }}
      >
        <Folder style={{ fontSize: 18 }} />
      </Box>
    </TableCell>
    <TableCell>
      <Typography
        variant="interactive-sm"
        sx={{
          color: COLORS.gray[900],
          fontWeight: 400,
          display: 'block',
          lineHeight: 1.3,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          '&:hover': { color: COLORS.purple[700], textDecoration: 'underline' },
        }}
      >
        {displayName}
      </Typography>
    </TableCell>
    <TableCell>
      {folder.count != null && (
        <Typography variant="p-sm" style={{ color: COLORS.gray[400] }}>
          {folder.count} {folder.count === 1 ? 'item' : 'items'}
        </Typography>
      )}
    </TableCell>
    <TableCell>
      <Stack direction="row" gap={0.5} onClick={(e) => e.stopPropagation()}>
        {canEdit && (
          <Tooltip title="Edit folder" placement="top" arrow>
            <Box component="span">
              <IconButton size="small" color="primary" aria-label="Edit folder" onClick={() => onEdit(folder)}>
                <Edit fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        )}
        {canDelete && (
          <Tooltip title="Delete folder" placement="top" arrow>
            <Box component="span">
              <IconButton size="small" color="primary" aria-label="Delete folder" onClick={() => onDelete(folder)}>
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        )}
      </Stack>
    </TableCell>
  </TableRow>
  );
};

export default React.memo(FolderTableRow);
